import { GetBetPanelDataUseCase } from "../lobby/domain/get-bet-panel-data.use-case";
import { GetBetButtonStateUseCase } from "./domain/get-bet-button-state.use-case";
import { Injectable, OnDestroy, TemplateRef } from "@angular/core";
import { IBetButtonState, IBetPanelData } from "./interfaces";
import {
    BehaviorSubject,
    distinctUntilChanged,
    filter,
    firstValueFrom,
    Observable,
    Subject,
    takeUntil
} from "rxjs";
import { IAutoplayOptions, ISendBetArgs, IUserBet } from "../core/round/store/interfaces";
import { cloneDeep, Dictionary } from "lodash";
import { GetRoundStateUseCase } from "../core/round/domain/get-round-state.use-case";
import { EBetSplit, ERoundState } from "../core/round/interfaces";
import { GetUserUseCase, GetWalletUseCase, IUser } from "@karma-games/angular-devkit/src/lib/modules/user";
import { map, take } from "rxjs/operators";
import { EToasterType, ToastService } from "@karma-games/angular-devkit/src/lib/modules/toast";
import { SendBetUseCase } from "../core/round/domain/send-bet.use-case";
import { CashOutUseCase } from "../core/round/domain/cash-out.use-case";
import { CancelBetUseCase } from "../core/round/domain/cancel-bet.use-case";
import { RemoveUserBetUseCase } from "../core/round/domain/remove-user-bet.use-case";
import { SetAutoplayUseCase } from "../core/round/domain/set-autoplay.use-case";
import { IPopupRef, PopupService } from "@karma-games/angular-devkit/src/lib/modules/popup";
import { StopAutoplayUseCase } from "./domain/stop-autoplay.use-case";
import { DomainErrors } from "@karma-games/angular-devkit/src/lib/classes/errors";
import { AddUserBetUseCase } from "../core/round/domain/add-user-bet.use-case";
import { GetRoundIdUseCase } from "../core/round/domain/get-round-id.use-case";
import { GetAutoCashOutUseCase } from "./domain/get-auto-cash-out.use-case";
import { GetRoundMultiplierUseCase } from "../core/round/domain/get-round-multiplier.use-case";
import { GetUserBetByIndexUseCase } from "../core/round/domain/get-user-bet-by-index.use-case";
import { UpdateBalanceViewUseCase } from "@karma-games/angular-devkit/src/lib/services/presenter-cluster";
import BetsNoLongerAccepted = DomainErrors.BetsNoLongerAccepted;
import UnsufficientBalanceError = DomainErrors.UnsufficientBalanceError;
import StopReceivedAfterGameEnd = DomainErrors.StopReceivedAfterGameEnd;
import BetAlreadyExists = DomainErrors.BetAlreadyExists;
import BetDoesntExist = DomainErrors.BetDoesntExist;
import { LobbyConnectionStateHandlerService } from '../lobby/lobby-connection-state-handler.service';

@Injectable()
export class BetPanelsService implements OnDestroy {
    private buttonStateChange$: BehaviorSubject<Dictionary<IBetButtonState>> = new BehaviorSubject<Dictionary<IBetButtonState>>({});
    private destroy$: Subject<void> = new Subject<void>();
    private currentRoundState: ERoundState = ERoundState.Idle;
    private autoplayOptionsPopupRef: IPopupRef | undefined = undefined;

    // TODO debug only..remove
    private debugMultiplier: number | undefined = undefined;
    private debugBetIndex: number | undefined = undefined;
    private debugBetAmount: number | undefined = undefined;

    constructor(private getBetPanelDataUseCase: GetBetPanelDataUseCase,
                private getBetButtonStateUseCase: GetBetButtonStateUseCase,
                private getRoundStateUseCase: GetRoundStateUseCase,
                private sendBetUseCase: SendBetUseCase,
                private cashOutUseCase: CashOutUseCase,
                private cancelBetUseCase: CancelBetUseCase,
                private removeUserBetUseCase: RemoveUserBetUseCase,
                private setAutoplayUseCase: SetAutoplayUseCase,
                private getWalletUseCase: GetWalletUseCase,
                private toast: ToastService,
                private popup: PopupService,
                private stopAutoplayUseCase: StopAutoplayUseCase,
                private addUserBetUseCase: AddUserBetUseCase,
                private getUserUseCase: GetUserUseCase,
                private getRoundIdUseCase: GetRoundIdUseCase,
                private connectionStateHandlerService: LobbyConnectionStateHandlerService,
                private getAutoCashOutUseCase: GetAutoCashOutUseCase,
                private getRoundMultiplierUseCase: GetRoundMultiplierUseCase,
                private getUserBetByIndexUseCase: GetUserBetByIndexUseCase,
                private updateBalanceViewUseCase: UpdateBalanceViewUseCase) {
        this.connectionStateHandlerService.onReconnect$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.removeUserBetUseCase.execute(0);
            this.removeUserBetUseCase.execute(1);
        })
        this.getRoundMultiplierUseCase.execute().pipe(takeUntil(this.destroy$)).subscribe(this.onMultiplierUpdate);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
    }

    getBetPanelData(): Observable<IBetPanelData> {
        return this.getBetPanelDataUseCase.execute();
    }

    initializeBetButtonListeners(betIndexes: number[]): void {
        for (let index of betIndexes) {
            this.getBetButtonStateUseCase.execute(index)
                .pipe(takeUntil(this.destroy$))
                .subscribe((buttonState: IBetButtonState) => {
                    let buttonStates: Dictionary<IBetButtonState> = {
                        ...this.buttonStateChange$.getValue(),
                        [index]: buttonState
                    };
                    this.buttonStateChange$.next(buttonStates);
                });

            this.getRoundStateUseCase.execute()
                .pipe(takeUntil(this.destroy$))
                .subscribe((state: ERoundState) => this.currentRoundState = state);

            this.getAutoCashOutUseCase.execute(index)
                .pipe(takeUntil(this.destroy$))
                .subscribe((userBet: IUserBet) => {
                    // cash out in case of auto stop multiplier applied (cash out is done on the server, this is only for changing the bet panel state)
                    this.cashOut(index, EBetSplit.Full, userBet.roundId, true);
                });

            this.getRoundStateUseCase.execute().pipe(
                distinctUntilChanged((prev: ERoundState, curr: ERoundState) => prev[1] === curr[1]))
                .subscribe(async (roundState: ERoundState) => {
                    if (roundState === ERoundState.Idle) {
                        const userBet: IUserBet | undefined = await firstValueFrom(this.getUserBetByIndexUseCase.execute(index));
                        if (userBet && !userBet.roundId) {
                            this.sendBet({
                                amount: userBet.amount,
                                betIndex: userBet.betIndex,
                                maxMultiplier: userBet.maxMultiplier
                            });
                        }
                    }
                });
        }
    }

    subscribeBetButtonsStates(): Observable<Dictionary<IBetButtonState>> {
        return this.buttonStateChange$.asObservable();
    }

    openAutoplayOptionsPopup(template: TemplateRef<any>): void {
        this.autoplayOptionsPopupRef = this.popup.showPopupInstantly({
            content: template,
            options: {
                customCssClass: "autoplay-options-popup",
                styleProperties: {
                    maxWidth: "11em",
                    width: "98%",
                    height: "auto",
                    overflow: "auto"
                }
            }
        });
    }

    async startAutoplay(args: Partial<IAutoplayOptions>): Promise<void> {
        const optionsWithoutBet: Partial<IAutoplayOptions> = cloneDeep(args);
        delete optionsWithoutBet.betArgs;
        if (!Object.keys(optionsWithoutBet).length) {
            this.toast.show({
                type: EToasterType.Warning,
                translationKey: "AUTOPLAY_OPTIONS_REQUIRED"
            });
            return;
        } else if (!args.roundCount) {
            this.toast.show({
                type: EToasterType.Warning,
                translationKey: "ROUND_COUNT_OPTION_REQUIRED"
            });
            return;
        }
        this.autoplayOptionsPopupRef?.hide();
        const wallet: number | undefined = await firstValueFrom(this.getUserUseCase.execute().pipe(map((user: IUser) => user.balance)));
        const autoPlayOptions: IAutoplayOptions = {
            ...(<IAutoplayOptions>args),
            initialWalletAmount: wallet ?? 0
        };
        this.setAutoplayUseCase.execute(autoPlayOptions);
    };

    async sendBet(args: ISendBetArgs): Promise<void> {
        if (this.currentRoundState !== ERoundState.Idle) {
            this.addUserBetUseCase.execute(args);
            return Promise.resolve();
        }
        try {
            await this.sendBetUseCase.execute(args);
            this.updateBalanceViewUseCase.execute({
                amount: - args.amount,
                animated: false
            });
        } catch (e) {
            console.error(e);
            this.removeUserBetUseCase.execute(args.betIndex);
            if (e instanceof BetsNoLongerAccepted) {
                // adding a bet for the next round in case the bet is not accepted due to BetsNoLongerAccepted error
                this.getRoundStateUseCase.execute()
                    .pipe(filter((state: ERoundState) => state !== ERoundState.Idle), take(1))
                    .subscribe(() => {
                        this.addUserBetUseCase.execute(args);
                    });
                this.toast.show({
                    type: EToasterType.Warning,
                    translationKey: "BET_ERRORS.BETS_NO_LONGER_ACCEPTED"
                });
            } else if (e instanceof UnsufficientBalanceError) {
                this.fetchBalance();
                this.toast.show({
                    type: EToasterType.Warning,
                    translationKey: "INSUFFICIENT_FUNDS_WARNING"
                });
            } else if (e instanceof BetAlreadyExists) {
                this.toast.show({
                    type: EToasterType.Error,
                    translationKey: "BET_ERRORS.BET_ALREADY_EXISTS"
                });
            } else {
                this.toast.show({
                    type: EToasterType.Error,
                    translationKey: "BET_ERRORS.BET_FAILED"
                });
            }
        }
    };

    private async fetchBalance(): Promise<void> {
        try {
            await this.getWalletUseCase.execute();
        } catch (e) {
            this.toast.show({
                type: EToasterType.Error,
                translationKey: "ERROR_BALANCE_FETCH"
            });
        }
    }

    async cancelBet(betIndex: number): Promise<void> {
        if (this.currentRoundState !== ERoundState.Idle) {
            this.removeUserBetUseCase.execute(betIndex);
            return Promise.resolve();
        }
        try {
            const userBet: IUserBet | undefined = await firstValueFrom(this.getUserBetByIndexUseCase.execute(betIndex));
            await this.cancelBetUseCase.execute(betIndex);
            if (userBet) {
                this.updateBalanceViewUseCase.execute({
                    amount: userBet.amount,
                    animated: false
                });
            }
        } catch (e) {
            if (e instanceof BetDoesntExist) {
                this.toast.show({
                    type: EToasterType.Error,
                    translationKey: "BET_ERRORS.BET_DOESNT_EXIST",
                });
            } else {
                this.toast.show({
                    type: EToasterType.Error,
                    translationKey: "BET_ERRORS.CANCEL_BET_FAILED"
                });
            }
        }
    };

    async cashOut(betIndex: number, split: EBetSplit = EBetSplit.Full, roundId?: string, isAutoStop?: boolean): Promise<void> {
        try {
            const currentRoundId: string | undefined = await firstValueFrom(this.getRoundIdUseCase.execute());
            await this.cashOutUseCase.execute({
                betIndex,
                roundId: <string>(roundId ?? currentRoundId),
                split,
                isAutoStop
            });
        } catch (e) {
            if (e instanceof StopReceivedAfterGameEnd) {
                // none
            } else if (e instanceof BetDoesntExist) {
                this.toast.show({
                    type: EToasterType.Error,
                    translationKey: "BET_ERRORS.BET_DOESNT_EXIST",
                });
            } else {
                this.toast.show({
                    type: EToasterType.Error,
                    translationKey: "BET_ERRORS.CASH_OUT_BET_FAILED"
                });
            }
        }
    };

    removeAutoplay(betIndex: number): void {
        this.stopAutoplayUseCase.execute(betIndex);
    }

    public setAutoStopDebug(multiplier: number, betIndex: number, betAmount: number): void {
        this.debugMultiplier = multiplier;
        this.debugBetIndex = betIndex;
        this.debugBetAmount = betAmount;
        this.sendBet({
            betIndex: this.debugBetIndex,
            amount: this.debugBetAmount,
            maxMultiplier: undefined
        });
        console.warn("DEBUG BET: ", {
            betIndex: this.debugBetIndex,
            amount: this.debugBetAmount,
            maxMultiplier: undefined
        }, "Multiplier set to: ", this.debugMultiplier);
    }

    private onMultiplierUpdate = async (multiplier: number): Promise<void> => {
        let bet;
        if (typeof this.debugBetIndex === "number") {
            bet = await firstValueFrom(this.getUserBetByIndexUseCase.execute(this.debugBetIndex));
        }
        if (bet && bet.roundId &&
            typeof this.debugMultiplier === "number" &&
            typeof this.debugBetAmount === "number" &&
            typeof this.debugBetIndex === "number" &&
            this.debugMultiplier === multiplier) {
            console.warn("DEBUG CASH OUT", multiplier);
            this.cashOut(this.debugBetIndex);
            this.debugMultiplier = undefined;
            this.debugBetAmount = undefined;
            this.debugBetIndex = undefined;
        }
    };
}
